import React, { useState } from "react";
import { toast } from "sonner";
import { PenLine } from "lucide-react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";

const fmtDT = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
export const fmtDay = (s) => new Date(`${s}T00:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

function ReportEditor({ initial = "", onSave, onCancel, testId }) {
    const [text, setText] = useState(initial);
    const [saving, setSaving] = useState(false);
    const save = async () => { setSaving(true); try { await onSave(text); } finally { setSaving(false); } };
    return (
        <div className="space-y-2" data-testid={testId}>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} autoFocus
                placeholder="Cosa è successo, cosa hai scoperto, decisioni prese, problemi incontrati…"
                data-testid={`${testId}-textarea`}
                className="w-full text-sm border border-neutral-200 rounded-md p-3 focus:outline-none focus:ring-1 focus:ring-neutral-400" />
            <div className="flex gap-2 justify-end">
                {onCancel && <button onClick={onCancel} data-testid={`${testId}-cancel`} className="text-xs px-3 py-1.5 rounded-md border border-neutral-200 hover:bg-neutral-50">Annulla</button>}
                <button onClick={save} disabled={saving || !text.trim()} data-testid={`${testId}-save`}
                    className="text-xs px-3 py-1.5 rounded-md bg-neutral-900 text-white disabled:opacity-40">{saving ? "Salvataggio…" : "Salva rapporto"}</button>
            </div>
        </div>
    );
}

function ReportCard({ r, mine, onUpdated }) {
    const [editing, setEditing] = useState(false);
    const save = async (content) => {
        try { await api.patch(`/timemachine/reports/${r.id}`, { content }); setEditing(false); onUpdated(); toast.success("Rapporto aggiornato"); }
        catch (e) { toast.error(extractError(e)); }
    };
    const name = `${r.author?.first_name || ""} ${r.author?.last_name || ""}`.trim() || "Utente";
    return (
        <div className="border border-neutral-200 rounded-md p-4 bg-white" data-testid={`report-${r.id}`}>
            {editing ? <ReportEditor initial={r.content} onSave={save} onCancel={() => setEditing(false)} testId={`report-edit-${r.id}`} />
                : <div className="text-sm text-neutral-800 whitespace-pre-wrap leading-relaxed" data-testid={`report-content-${r.id}`}>{r.content}</div>}
            <div className="mt-4 pt-3 border-t border-neutral-100 flex items-center gap-3">
                <UserAvatar user={r.author} size={28} />
                <div className="text-xs leading-tight flex-1" data-testid={`report-signature-${r.id}`}>
                    <div className="font-medium text-neutral-800">{name}</div>
                    <div className="text-neutral-500">Scritto il {fmtDT(r.created_at)}{r.edited_at ? ` · modificato il ${fmtDT(r.edited_at)}` : ""}</div>
                </div>
                {mine && !editing && (
                    <button onClick={() => setEditing(true)} data-testid={`report-edit-btn-${r.id}`} className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1">
                        <PenLine size={12} /> Modifica
                    </button>
                )}
            </div>
        </div>
    );
}

export function DailyReports({ day, reports, onChange, emptyHint }) {
    const { user } = useAuth();
    const [writing, setWriting] = useState(false);
    const mineExists = reports.some((r) => r.author_id === user?.id);
    const create = async (content) => {
        try { await api.post("/timemachine/reports", { date: day, content }); setWriting(false); onChange(); toast.success("Rapporto salvato"); }
        catch (e) { toast.error(extractError(e)); }
    };
    return (
        <div className="space-y-3">
            {reports.map((r) => <ReportCard key={r.id} r={r} mine={r.author_id === user?.id} onUpdated={onChange} />)}
            {day && !mineExists && (writing
                ? <div className="border border-neutral-200 rounded-md p-4 bg-white"><ReportEditor onSave={create} onCancel={() => setWriting(false)} testId="new-report" /></div>
                : (
                    <div className={`${reports.length === 0 ? "border border-dashed border-neutral-300 rounded-md p-6 text-center bg-white" : ""}`}>
                        {reports.length === 0 && emptyHint && <div className="text-sm text-neutral-500 mb-3" data-testid="tm-empty-hint">{emptyHint}</div>}
                        <button onClick={() => setWriting(true)} data-testid="write-report-btn"
                            className="text-xs px-3 py-1.5 rounded-md bg-neutral-900 text-white inline-flex items-center gap-1.5">
                            <PenLine size={12} /> Scrivi il rapporto di {fmtDay(day)}
                        </button>
                    </div>
                ))}
        </div>
    );
}
